import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import PreFattibilita from "../components/PreFattibilita";
import RoofMap from "../components/RoofMap";
import EvBenefitEditor from "../components/EvBenefitEditor";
import { PartnerQuote } from "../components/PartnerQuote";
import { PartnerPicker } from "../components/PartnerPicker";
import LandingQuickActions from "../components/LandingQuickActions";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { ArrowRight, ArrowLeft, Upload, Loader2, CheckCircle2, FileText, Gift, Clock, ShieldCheck, Lock, Sun, TrendingUp, Zap, Wallet, BarChart3, Lightbulb, PlayCircle, AlertTriangle, ChevronDown } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { EnergyChartLegend, withAcquistata } from "../components/EnergyChartLegend";
import { ConsensiPrivacy, CONSENSI_KEYS } from "../components/ConsensiPrivacy";

const STEPS = ["I tuoi dati", "Pre-fattibilità", "La tua bolletta", "Produzione & guadagno", "Il tuo tetto", "Fatto!"];

const PROVINCES = [
  ["AG","Agrigento"],["AL","Alessandria"],["AN","Ancona"],["AO","Aosta"],["AR","Arezzo"],["AP","Ascoli Piceno"],
  ["AT","Asti"],["AV","Avellino"],["BA","Bari"],["BT","Barletta-Andria-Trani"],["BL","Belluno"],["BN","Benevento"],
  ["BG","Bergamo"],["BI","Biella"],["BO","Bologna"],["BZ","Bolzano"],["BS","Brescia"],["BR","Brindisi"],["CA","Cagliari"],
  ["CL","Caltanissetta"],["CB","Campobasso"],["CE","Caserta"],["CT","Catania"],["CZ","Catanzaro"],["CH","Chieti"],
  ["CO","Como"],["CS","Cosenza"],["CR","Cremona"],["KR","Crotone"],["CN","Cuneo"],["EN","Enna"],["FM","Fermo"],
  ["FE","Ferrara"],["FI","Firenze"],["FG","Foggia"],["FC","Forlì-Cesena"],["FR","Frosinone"],["GE","Genova"],
  ["GO","Gorizia"],["GR","Grosseto"],["IM","Imperia"],["IS","Isernia"],["AQ","L'Aquila"],["SP","La Spezia"],
  ["LT","Latina"],["LE","Lecce"],["LC","Lecco"],["LI","Livorno"],["LO","Lodi"],["LU","Lucca"],["MC","Macerata"],
  ["MN","Mantova"],["MS","Massa-Carrara"],["MT","Matera"],["ME","Messina"],["MI","Milano"],["MO","Modena"],
  ["MB","Monza e Brianza"],["NA","Napoli"],["NO","Novara"],["NU","Nuoro"],["OR","Oristano"],["PD","Padova"],
  ["PA","Palermo"],["PR","Parma"],["PV","Pavia"],["PG","Perugia"],["PU","Pesaro e Urbino"],["PE","Pescara"],
  ["PC","Piacenza"],["PI","Pisa"],["PT","Pistoia"],["PN","Pordenone"],["PZ","Potenza"],["PO","Prato"],["RG","Ragusa"],
  ["RA","Ravenna"],["RC","Reggio Calabria"],["RE","Reggio Emilia"],["RI","Rieti"],["RN","Rimini"],["RM","Roma"],
  ["RO","Rovigo"],["SA","Salerno"],["SS","Sassari"],["SV","Savona"],["SI","Siena"],["SR","Siracusa"],["SO","Sondrio"],
  ["SU","Sud Sardegna"],["TA","Taranto"],["TE","Teramo"],["TR","Terni"],["TO","Torino"],["TP","Trapani"],["TN","Trento"],
  ["TV","Treviso"],["TS","Trieste"],["UD","Udine"],["VA","Varese"],["VE","Venezia"],["VB","Verbano-Cusio-Ossola"],
  ["VC","Vercelli"],["VR","Verona"],["VV","Vibo Valentia"],["VI","Vicenza"],["VT","Viterbo"],
];

function ProvinceSelect() { return null; }


const TRUST = [
  { icon: Gift, title: "Gratis e senza impegno", desc: "Nessun costo e nessun obbligo di acquisto." },
  { icon: Clock, title: "In pochi minuti", desc: "Analisi personalizzata in autonomia." },
  { icon: ShieldCheck, title: "Made in Italy", desc: "Pannelli e impianti TORRI.solar." },
  { icon: Lock, title: "Dati al sicuro", desc: "Trattati secondo l'informativa privacy." },
];

const eur = (v) => Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
const num = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Ricava città, provincia (sigla) e CAP da una stringa indirizzo, quando l'utente
// non ha scelto un suggerimento Google. Usa il Geocoder (Places già caricato dal campo indirizzo).
function geocodeAddress(address) {
  return new Promise((resolve) => {
    const g = window.google;
    if (!g?.maps?.Geocoder || !(address || "").trim()) return resolve(null);
    const geocoder = new g.maps.Geocoder();
    geocoder.geocode({ address, componentRestrictions: { country: "it" } }, (results, status) => {
      if (status !== "OK" || !results || !results[0]) return resolve(null);
      const comps = results[0].address_components || [];
      const get = (t) => comps.find((c) => c.types.includes(t));
      const citta = get("locality")?.long_name || get("administrative_area_level_3")?.long_name || get("postal_town")?.long_name || "";
      const provincia = (get("administrative_area_level_2")?.short_name || "").toUpperCase();
      const cap = get("postal_code")?.long_name || "";
      resolve({ citta, provincia, cap });
    });
  });
}

const INTERVISTA = [
  { key: "tipo_immobile", label: "Che tipo di immobile vuoi alimentare?", options: ["Casa singola", "Condominio", "Azienda", "Terreno / Utility"] },
  { key: "installazione", label: "Dove verrà installato l'impianto?", options: ["Tetto tradizionale", "Tetto piano", "Pensilina / Carport", "Terreno", "Altro"] },
  { key: "situazioni", label: "Sono presenti situazioni particolari?", multi: true, options: ["Amianto", "Vincoli paesaggistici", "Cabina MT", "Nessuna di queste"] },
];

// Risposte che richiedono la valutazione del team tecnico (bloccanti per l'autonomia).
const IV_BLOCKING = {
  tipo_immobile: { opts: ["Azienda", "Terreno / Utility"], reason: "progetto non residenziale" },
  installazione: { opts: ["Pensilina / Carport", "Terreno", "Altro"], reason: "installazione speciale (non tetto standard)" },
  impianto_esistente: { opts: ["Sì, da sostituire (Revamping)"], reason: "sostituzione impianto esistente (revamping)" },
  situazioni: { opts: ["Amianto", "Vincoli paesaggistici", "Cabina MT"], reason: "condizione tecnica particolare" },
};

const computeIvBlocking = (iv) => {
  const out = [];
  Object.entries(IV_BLOCKING).forEach(([key, cfg]) => {
    const val = iv[key];
    const selected = Array.isArray(val) ? val : (val ? [val] : []);
    selected.forEach((o) => { if (cfg.opts.includes(o)) out.push({ answer: o, reason: cfg.reason }); });
  });
  return out;
};

// "Lo sapevi che?" — contenuto per fase (indice step)
const DID_YOU_KNOW = {
  0: {
    kicker: "Scenari di mercato",
    subtitle: "Le normative IEC non bastano a proteggere dalla bassa qualità.",
    big: "93%",
    intro: "Scegliere un pannello fotovoltaico economico è la causa principale dei problemi di sicurezza negli impianti fotovoltaici in Europa.",
    quote: "«Il 93% degli impianti presenta almeno un problema grave di sicurezza» (oltre 800 impianti ispezionati)",
    author: "Ankil Sanghvi, ingegnere di Clean Energy Associates (CEA)",
  },
  1: {
    kicker: "Scenari di mercato",
    subtitle: "Le normative IEC non bastano a proteggere dalla bassa qualità.",
    big: "100%",
    intro: "Un pannello fotovoltaico standard non protegge a sufficienza il silicio dalla grandine.",
    quote: "Nel 100% dei moduli ispezionati dopo una grandinata sono state rilevate cricche gravi tramite test di elettroluminescenza, sebbene a occhio nudo sembrassero integri, con una media di 5 celle danneggiate per modulo.",
    author: "Claire Kearns-McCoy, responsabile CEA per le ispezioni sui moduli",
  },
  2: {
    kicker: "Scenari di mercato",
    subtitle: "Le normative IEC non bastano a proteggere dalla bassa qualità.",
    big: "76%",
    intro: "L'installatore che calpesta i pannelli fotovoltaici durante l'installazione e la manutenzione è la causa principale dei problemi sui pannelli fotovoltaici.",
    quote: "Il 76% degli impianti ispezionati presenta celle con rotture complesse.",
    author: "Clean Energy Associates, 2024",
    link: "https://youtu.be/wL2ZlHTXRcw",
    linkLabel: "Guarda il video completo",
    embed: { id: "wL2ZlHTXRcw", start: 17, end: 35 },
  },
  3: {
    variant: "tech",
    kicker: "La nostra tecnologia",
    title: "Tecnologia PRISMA 4.0",
    intro: "TORRI. produce pannelli fotovoltaici in Italia dal 2007. Le tecnologie proprietarie lo rendono il pannello fotovoltaico più sicuro, robusto e longevo del mercato.",
    subtitle: "Sulla base dei risultati ottenuti e di un modello statistico fenomenologico, PRISMA 4.0 è soggetta ai fenomeni esterni per meno di 2/3 rispetto a un prodotto tradizionale.",
    stats: [
      { big: "8x", label: "Resistenza alla grandine rispetto allo standard di mercato" },
      { big: "3x", label: "Resistenza al carico neve, fino a 750 kg/m²" },
      { big: "−25%", label: "Rischio incendi" },
      { big: "−25%", label: "Perdita di performance" },
    ],
    embed: { id: "PuqkFKvBDCU", start: 3, end: 11 },
  },
};

export default function ConfiguraWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selfToken, setSelfToken] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [busy, setBusy] = useState(false);
  const startedAtRef = useRef(Date.now());
  const [hp, setHp] = useState("");

  // step 0 dati
  const [f, setF] = useState({ nome: "", cognome: "", email: "", phone: "", address: "", citta: "", provincia: "", cap: "", entity_type: "persona_fisica", ragione_sociale: "", partita_iva: "", codice_fiscale: "" });
  const [partner, setPartner] = useState({ via_partner: false, partner_id: "", partner_name: "", partner_logo_url: "" });
  const [consensi, setConsensi] = useState({ servizio: false, statistico: false, marketing: false });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const isAzienda = f.entity_type === "azienda";
  // Selezione da Google Places: compila automaticamente via/civico, città, provincia e CAP.
  const onAddressSelect = (addr, place) => {
    const comps = place?.address_components || [];
    const get = (type) => comps.find((c) => c.types.includes(type));
    const route = get("route")?.long_name || "";
    const civico = get("street_number")?.long_name || "";
    const city = get("locality")?.long_name || get("administrative_area_level_3")?.long_name || get("postal_town")?.long_name || "";
    const prov = (get("administrative_area_level_2")?.short_name || "").toUpperCase();
    const cap = get("postal_code")?.long_name || "";
    const full = place?.formatted_address || addr || "";
    setF((s) => ({
      ...s,
      address: full || (route ? `${route}${civico ? ", " + civico : ""}` : s.address),
      citta: city || s.citta,
      provincia: PROVINCES.some(([c]) => c === prov) ? prov : s.provincia,
      cap: cap || s.cap,
    }));
  };

  // step 2 bolletta
  const fileRef = useRef(null);
  const [billInfo, setBillInfo] = useState(null);
  const [manualKwh, setManualKwh] = useState("");

  // step 3 produzione & guadagno
  const [summary, setSummary] = useState(null);
  const [prodBusy, setProdBusy] = useState(false);
  const [countdown, setCountdown] = useState(120);
  useEffect(() => {
    if (!busy && !prodBusy) return;
    setCountdown(120);
    const id = setInterval(() => setCountdown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [busy, prodBusy]);
  const countLabel = `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`;
  const [prodError, setProdError] = useState(null);
  const [manualCity, setManualCity] = useState("");

  // step 4 tetto
  const [roofSummary, setRoofSummary] = useState(null);

  // step 1 intervista
  const [intervista, setIntervista] = useState({ situazioni: [] });
  const [ivBlocking, setIvBlocking] = useState([]);
  const selectAns = (q, o) => setIntervista((s) => {
    if (q.multi) {
      if (o === "Nessuna di queste") {
        const has = (s[q.key] || []).includes(o);
        return { ...s, [q.key]: has ? [] : ["Nessuna di queste"] };
      }
      const cur = new Set(s[q.key] || []);
      cur.delete("Nessuna di queste");
      cur.has(o) ? cur.delete(o) : cur.add(o);
      return { ...s, [q.key]: [...cur] };
    }
    return { ...s, [q.key]: o };
  });
  const saveIntervista = async () => {
    const required = ["tipo_immobile", "installazione"];
    if (required.some((k) => !intervista[k]) || !(intervista.situazioni && intervista.situazioni.length)) {
      return toast.error("Rispondi a tutte le domande per continuare");
    }
    setBusy(true);
    try {
      await api.put(`/public/self/${selfToken}/intervista`, {
        tipo_immobile: intervista.tipo_immobile || null,
        installazione: intervista.installazione || null,
        impianto_esistente: null,
        situazioni: intervista.situazioni || [],
        tempistica: null,
      });
      setIvBlocking(computeIvBlocking(intervista));
      setStep(2);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const start = async () => {
    if (isAzienda) {
      if (!f.ragione_sociale.trim()) return toast.error("Inserisci la ragione sociale");
      if (f.partita_iva.trim() && !/^\d{11}$/.test(f.partita_iva.replace(/\s/g, ""))) return toast.error("La Partita IVA inserita non è valida (11 cifre)");
      if (!f.nome.trim() || !f.cognome.trim()) return toast.error("Inserisci la persona di riferimento (nome e cognome)");
    } else {
      if (!f.nome.trim() || !f.cognome.trim()) return toast.error("Inserisci nome e cognome");
    }
    if (!f.email.includes("@")) return toast.error("Inserisci un'email valida");
    if (!f.phone.trim()) return toast.error("Inserisci un numero di telefono");
    if (!f.address.trim()) return toast.error("Inserisci l'indirizzo dell'immobile");
    if (!CONSENSI_KEYS.every((k) => consensi[k])) return toast.error("Per proseguire devi accettare tutti i consensi privacy");
    setBusy(true);
    try {
      let citta = f.citta, provincia = f.provincia, cap = f.cap;
      if (!citta || !provincia) {
        const geo = await geocodeAddress(f.address);
        if (geo) {
          citta = geo.citta || citta; provincia = geo.provincia || provincia; cap = cap || geo.cap;
          setF((s) => ({ ...s, citta, provincia, cap }));
        }
      }
      const { data } = await api.post("/public/self/start", { ...f, citta, provincia, cap, ...partner, consenso_privacy: true, consensi, hp, elapsed_ms: Date.now() - startedAtRef.current });
      setSelfToken(data.self_token); setLeadId(data.lead_id); setStep(1);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadBill = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/public/self/${selfToken}/bill`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBillInfo(data); setSummary(null);
      toast.success("Bolletta analizzata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Analisi non riuscita, prova con l'inserimento manuale"); }
    finally { setBusy(false); }
  };

  const saveManual = async () => {
    const kwh = Number(manualKwh);
    if (!kwh || kwh <= 0) return toast.error("Inserisci il consumo annuo in kWh");
    setBusy(true);
    try {
      await api.put(`/public/self/${selfToken}/analysis`, { consumo_totale_kwh: kwh, periodo: "annuo" });
      setBillInfo({ consumo_annuo_kwh: kwh, pv_kwp: null }); setSummary(null);
      toast.success("Consumo salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runProduction = async (manualLoc) => {
    setProdBusy(true); setProdError(null);
    try {
      await api.post(`/public/self/${selfToken}/pvgis`, manualLoc ? { address: manualLoc } : {});
      const { data } = await api.get(`/public/self/${selfToken}/summary`);
      setSummary(data);
    } catch (e) {
      setProdError(formatApiError(e.response?.data?.detail) || "Non riusciamo a individuare la tua località. Inserisci il tuo comune qui sotto.");
    } finally { setProdBusy(false); }
  };

  useEffect(() => {
    if (step === 3 && !summary && !prodBusy && !prodError) runProduction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Ad ogni cambio step il wizard torna in cima al form (altrimenti la nuova schermata si apre scrollata troppo in basso).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/public/self/${selfToken}/complete`);
      if (window.fbq) window.fbq("track", "Lead", { content_name: "Configuratore completato" });
      navigate(`/c/${data.consulenza_token}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const leadStub = { id: leadId, address: f.address, indirizzo_residenza: f.address, city: f.citta, roof: null, pre_fattibilita: null };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="mx-auto max-w-3xl px-5 py-4 flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="funnel-logo-home"
            aria-label="Vai alla pagina principale"
            className="flex items-center gap-2 rounded-md transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-9 w-9 rounded-full object-contain" />
            <div className="leading-tight text-left">
              <div className="font-heading font-extrabold tracking-wide">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-[0.15em]">Div. Chiavi in mano</div>
            </div>
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 py-6 sm:py-8">
        {/* stepper */}
        <div className="mb-8" data-testid="wizard-stepper">
          <div className="sm:hidden">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-primary">Passo {step + 1} di {STEPS.length}</span>
              <span className="text-xs text-muted-foreground">{STEPS[step]}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {STEPS.map((s, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-border"}`} />
              ))}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={i} className="flex-1">
                <div className={`h-1.5 rounded-full transition-colors ${i <= step ? "bg-primary" : "bg-border"}`} />
                <div className={`mt-1.5 text-xs ${i === step ? "text-primary font-semibold" : "text-muted-foreground"}`}>{s}</div>
              </div>
            ))}
          </div>
        </div>

        {step === 0 && (
          <Card className="rounded border-border p-6" data-testid="step-dati">
            <h1 className="font-heading text-2xl font-extrabold mb-1">Iniziamo dai tuoi dati</h1>
            <p className="text-sm text-muted-foreground mb-6">Ti guideremo passo passo fino alla tua consulenza personalizzata.</p>
            <div className="mb-6 pb-6 border-b border-border">
              <PartnerPicker value={partner} onChange={setPartner} />
            </div>
            <div className="mb-5">
              <label className="text-xs text-muted-foreground">Tipo di richiedente</label>
              <div className="mt-1.5 inline-flex rounded-lg border border-border p-1 bg-secondary/30" data-testid="entity-switch">
                {[["persona_fisica", "Privato"], ["azienda", "Azienda"]].map(([k, lbl]) => (
                  <button key={k} type="button" onClick={() => setF((s) => ({ ...s, entity_type: k }))} data-testid={`entity-${k}`}
                    className={`px-5 py-1.5 rounded-md text-sm font-semibold transition-colors ${f.entity_type === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {isAzienda && (
                <>
                  <div><label className="text-xs text-muted-foreground">Ragione sociale *</label><Input value={f.ragione_sociale} onChange={set("ragione_sociale")} data-testid="dati-ragione-sociale" /></div>
                  <div><label className="text-xs text-muted-foreground">Partita IVA</label><Input value={f.partita_iva} onChange={set("partita_iva")} placeholder="11 cifre (opzionale)" data-testid="dati-partita-iva" /></div>
                </>
              )}
              <div><label className="text-xs text-muted-foreground">{isAzienda ? "Persona di riferimento · Nome *" : "Nome *"}</label><Input value={f.nome} onChange={set("nome")} data-testid="dati-nome" /></div>
              <div><label className="text-xs text-muted-foreground">{isAzienda ? "Persona di riferimento · Cognome *" : "Cognome *"}</label><Input value={f.cognome} onChange={set("cognome")} data-testid="dati-cognome" /></div>
              <div><label className="text-xs text-muted-foreground">Email *</label><Input type="email" value={f.email} onChange={set("email")} data-testid="dati-email" /></div>
              <div><label className="text-xs text-muted-foreground">Telefono *</label><Input value={f.phone} onChange={set("phone")} data-testid="dati-phone" /></div>
              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground">Indirizzo completo, civico, città *</label>
                <AddressAutocomplete value={f.address} onChange={(v) => setF((s) => ({ ...s, address: v, citta: "", provincia: "", cap: "" }))} onSelect={onAddressSelect} placeholder="Es. Via Roma 12, Brescia" data-testid="dati-address" />
                <p className="text-[11px] text-muted-foreground mt-1">Digita e <b>seleziona l'indirizzo dai suggerimenti</b>: rileviamo automaticamente città, provincia e CAP.</p>
              </div>
            </div>
            {/* Honeypot anti-bot: nascosto agli utenti reali, compilato solo dai bot */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, overflow: "hidden" }}>
              <label>Sito web<input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} data-testid="dati-hp" /></label>
            </div>
            <ConsensiPrivacy value={consensi} onChange={setConsensi} />
            <Button id="btn-inizia-configurazione" onClick={start} disabled={busy} className="mt-6 w-full sm:w-auto" data-testid="dati-next">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Inizia la configurazione <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </Card>
        )}

        {step === 0 && (
          <div className="mt-20 sm:mt-28 border-t border-border pt-12 grid grid-cols-2 gap-3" data-testid="trust-badges">
            {TRUST.map((t, i) => (
              <div key={i} className="flex items-start gap-2.5 rounded-lg border border-border bg-secondary/30 p-3" data-testid={`trust-badge-${i}`}>
                <t.icon className="h-5 w-5 text-primary shrink-0 mt-0.5" strokeWidth={2} />
                <div className="leading-tight">
                  <div className="text-xs font-semibold">{t.title}</div>
                  <div className="text-[11px] text-muted-foreground">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {step === 0 && <LandingQuickActions />}

        {step === 1 && (
          <Card className="rounded border-border p-6" data-testid="step-intervista">
            <h1 className="font-heading text-2xl font-extrabold mb-1">Il tuo progetto</h1>
            <p className="text-sm text-muted-foreground mb-6">Poche domande per inquadrare al meglio il tuo progetto. <span className="text-primary">Tutte le domande sono obbligatorie.</span></p>
            {INTERVISTA.map((q) => (
              <div key={q.key} className="mb-6" data-testid={`iv-q-${q.key}`}>
                <div className="text-sm font-semibold mb-2">{q.label} <span className="text-primary">*</span></div>
                <div className="flex flex-wrap gap-2">
                  {q.options.map((o) => {
                    const active = q.multi ? (intervista[q.key] || []).includes(o) : intervista[q.key] === o;
                    return (
                      <button key={o} type="button" onClick={() => selectAns(q, o)} data-testid={`iv-${q.key}-${slug(o)}`}
                        className={`px-3.5 py-2 rounded-full border text-sm transition-colors ${active ? "border-primary bg-primary/15 text-primary font-semibold" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                        {o}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            <div className="flex items-center justify-between mt-2">
              <Button variant="ghost" onClick={() => setStep(0)} data-testid="intervista-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro</Button>
              <Button onClick={saveIntervista} disabled={busy} data-testid="intervista-next">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Avanti <ArrowRight className="h-4 w-4 ml-1.5" /></>}
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && (
          <Card className="rounded border-border p-6" data-testid="step-bolletta">
            <h1 className="font-heading text-2xl font-extrabold mb-1">La tua bolletta</h1>
            <p className="text-sm text-muted-foreground mb-6">Carica una bolletta: analizzeremo automaticamente i tuoi consumi e il costo dell'energia. Oppure inserisci il consumo annuo manualmente.</p>
            {ivBlocking.length > 0 && (
              <div className="mb-5 rounded-lg border border-amber-500/50 bg-amber-500/10 p-4" data-testid="blocking-notice">
                <div className="flex items-center gap-2 text-amber-500 font-semibold text-sm"><AlertTriangle className="h-4 w-4" /> Il tuo progetto richiede la valutazione del team tecnico</div>
                <ul className="mt-2 space-y-1 text-xs text-foreground/80">
                  {ivBlocking.map((b, i) => (
                    <li key={i} className="flex gap-2" data-testid={`blocking-item-${i}`}><span className="text-amber-500">•</span><span><b>{b.answer}</b> — {b.reason}</span></li>
                  ))}
                </ul>
                <p className="mt-2 text-[11px] text-muted-foreground">Puoi comunque proseguire: un nostro tecnico verificherà la fattibilità e ti ricontatterà.</p>
              </div>
            )}
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif" hidden onChange={(e) => uploadBill(e.target.files?.[0])} data-testid="bolletta-file" />
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="bolletta-upload">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Carica bolletta (foto o PDF)
            </Button>
            {busy && (
              <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2.5 text-sm text-red-600" data-testid="bolletta-analisi-alert" role="alert">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Analisi della bolletta in corso: <b>potrebbero volerci massimo 120 secondi</b>. Non chiudere la pagina.
                  <span className="mt-1.5 flex items-center gap-1.5 font-heading font-bold text-base tabular-nums" data-testid="bolletta-countdown"><Loader2 className="h-4 w-4 animate-spin" /> {countLabel}</span>
                </span>
              </div>
            )}
            <div className="my-4 text-xs text-muted-foreground">— oppure —</div>
            <div className="flex items-end gap-2">
              <div><label className="text-xs text-muted-foreground">Consumo annuo (kWh)</label><Input type="number" value={manualKwh} onChange={(e) => setManualKwh(e.target.value)} placeholder="es. 3500" className="w-40" data-testid="bolletta-manual" /></div>
              <Button variant="secondary" onClick={saveManual} disabled={busy} data-testid="bolletta-manual-save">Salva</Button>
            </div>
            {billInfo && (
              <div className="mt-5 rounded-lg border border-primary/40 bg-primary/10 p-4 text-sm" data-testid="bolletta-result">
                <div className="flex items-center gap-2 text-primary font-semibold"><CheckCircle2 className="h-4 w-4" /> Consumi rilevati</div>
                <div className="mt-1 text-foreground">Consumo annuo: <b>{Math.round(billInfo.consumo_annuo_kwh || 0).toLocaleString("it-IT")} kWh</b>{billInfo.pv_kwp ? <> · Impianto consigliato ~<b>{billInfo.pv_kwp} kWp</b></> : null}</div>
                {billInfo.prezzo_energia_kwh ? (
                  <div className="mt-1 text-foreground" data-testid="bolletta-prezzo">Prezzo energia rilevato: <b>{Number(billInfo.prezzo_energia_kwh).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €/kWh</b></div>
                ) : null}
                {billInfo.potenza_contatore_kw ? (
                  <div className="mt-1 text-foreground" data-testid="bolletta-potenza">Potenza contatore: <b>{Number(billInfo.potenza_contatore_kw).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kW</b></div>
                ) : null}
              </div>
            )}
            <div className="flex items-center justify-between mt-6">
              <Button variant="ghost" onClick={() => setStep(1)} data-testid="bolletta-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro</Button>
              <Button onClick={() => { setSummary(null); setProdError(null); setStep(3); }} disabled={!billInfo} data-testid="bolletta-next">Avanti <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
            </div>
          </Card>
        )}

        {step === 3 && (
          <div data-testid="step-produzione">
            <Card className="rounded border-border p-6">
              <div className="flex items-center gap-2 mb-1">
                <Sun className="h-5 w-5 text-primary" strokeWidth={2} />
                <h1 className="font-heading text-2xl font-extrabold">La tua produzione e il tuo guadagno</h1>
              </div>
              <p className="text-sm text-muted-foreground mb-3">Colleghiamo il servizio satellitare PVGIS alla tua località per stimare produzione, autoconsumo e risparmio in bolletta.</p>
              <p className="text-sm font-semibold text-red-500 flex items-center gap-1.5 mb-6" data-testid="pvgis-city-warning"><AlertTriangle className="h-4 w-4 shrink-0" /> Inserisci solo la <b>città o il comune</b> (es. Brescia), non l'indirizzo completo.</p>

              {prodBusy && (
                <div className="my-4 flex items-start gap-2 rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2.5 text-sm text-red-600" data-testid="produzione-loading" role="alert">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>Calcolo produzione e guadagno in corso via PVGIS: <b>potrebbero volerci massimo 120 secondi</b>. Non chiudere la pagina.
                    <span className="mt-1.5 flex items-center gap-1.5 font-heading font-bold text-base tabular-nums" data-testid="produzione-countdown"><Loader2 className="h-4 w-4 animate-spin" /> {countLabel}</span>
                  </span>
                </div>
              )}

              {!prodBusy && prodError && (
                <div className="text-sm" data-testid="produzione-error">
                  <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-foreground">{prodError}</div>
                  <div className="mt-4">
                    <label className="text-xs text-muted-foreground">Inserisci il tuo comune</label>
                    <div className="flex gap-2 mt-1">
                      <Input value={manualCity} onChange={(e) => setManualCity(e.target.value)} placeholder="es. Brescia" data-testid="prod-manual-city" onKeyDown={(e) => { if (e.key === "Enter" && manualCity.trim()) runProduction(manualCity.trim()); }} />
                      <Button onClick={() => runProduction(manualCity.trim())} disabled={!manualCity.trim()} data-testid="prod-manual-search">Cerca</Button>
                    </div>
                    <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1 mt-1.5" data-testid="prod-city-warning"><AlertTriangle className="h-3 w-3 shrink-0" /> Indica solo la città o il comune (es. Brescia), non l'indirizzo completo.</p>
                  </div>
                  <Button variant="ghost" onClick={() => runProduction()} className="mt-2" data-testid="produzione-retry">Riprova con l'indirizzo iniziale</Button>
                </div>
              )}

              {!prodBusy && summary && (
                <div className="space-y-5" data-testid="produzione-result">
                  {summary.location && <p className="text-xs text-muted-foreground">Località: <b>{summary.location}</b> · fonte produzione PVGIS</p>}

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid="prod-kwp">
                      <div className="text-[11px] text-muted-foreground">Impianto consigliato</div>
                      <div className="text-lg font-heading font-bold">{summary.pv_kwp ?? "—"} <span className="text-xs font-normal">kWp</span></div>
                    </div>
                    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid="prod-produzione">
                      <div className="text-[11px] text-muted-foreground">Produzione annua</div>
                      <div className="text-lg font-heading font-bold">{num(summary.produzione_annua_kwh)} <span className="text-xs font-normal">kWh</span></div>
                    </div>
                    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid="prod-autoconsumo">
                      <div className="text-[11px] text-muted-foreground">Autoconsumo</div>
                      <div className="text-lg font-heading font-bold">{summary.autoconsumo_pct ?? "—"}<span className="text-xs font-normal">%</span></div>
                    </div>
                    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid="prod-copertura">
                      <div className="text-[11px] text-muted-foreground">Copertura fabbisogni</div>
                      <div className="text-lg font-heading font-bold">{summary.copertura_pct ?? "—"}<span className="text-xs font-normal">%</span></div>
                    </div>
                  </div>

                  {summary.monthly?.length > 0 && (
                    <div data-testid="prod-mensile">
                      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        <BarChart3 className="h-4 w-4 text-primary" /> Analisi mensile · produzione e consumo
                      </div>
                      <div className="h-[260px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={withAcquistata(summary.monthly)} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
                            <XAxis dataKey="mese" tick={{ fill: "#9ca3af", fontSize: 10 }} axisLine={{ stroke: "rgba(255,255,255,0.1)" }} tickLine={false} />
                            <YAxis tick={{ fill: "#9ca3af", fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
                            <Tooltip contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#e5e7eb" }} formatter={(v, n) => [`${Math.round(v)} kWh`, n]} />
                            <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                            <Bar dataKey="produzione" name="Produzione FV" fill="#FDD003" radius={[3, 3, 0, 0]} maxBarSize={18} />
                            <Bar dataKey="consumo" name="Consumo" fill="#64748b" radius={[3, 3, 0, 0]} maxBarSize={18} />
                            <Line type="monotone" dataKey="autoconsumo" name="Autoconsumo" stroke="#34D399" strokeWidth={2.5} dot={{ r: 2, fill: "#34D399" }} />
                            <Line type="monotone" dataKey="acquistata" name="Energia acquistata" stroke="#EF4444" strokeWidth={2} strokeDasharray="5 4" dot={false} />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                      <EnergyChartLegend data={summary.monthly} colors={{ prod: "#FDD003", cons: "#64748b", auto: "#34D399", acq: "#EF4444" }} hasBattery={!!summary.storage_kwh} />
                    </div>
                  )}

                  <div className="rounded-lg border border-border p-3 text-sm flex items-center gap-2" data-testid="prod-prezzo">
                    <Zap className="h-4 w-4 text-primary" />
                    <span>Costo energia in bolletta: <b>{Number(summary.prezzo_usato || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €/kWh</b> · <span className="text-muted-foreground">{summary.prezzo_energia_kwh ? "rilevato dalla bolletta" : "stima media di mercato"}</span>{summary.potenza_contatore_kw ? <> · Potenza contatore: <b>{Number(summary.potenza_contatore_kw).toLocaleString("it-IT", { maximumFractionDigits: 2 })} kW</b></> : null}</span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="rounded-xl border border-primary/40 bg-primary/10 p-5" data-testid="prod-guadagno-anno">
                      <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wide"><Wallet className="h-4 w-4" /> Guadagno primo anno</div>
                      <div className="text-3xl font-heading font-extrabold mt-1">{eur(summary.guadagno_anno)}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">Risparmio in bolletta + energia immessa in rete.</div>
                    </div>
                    <div className="rounded-xl border border-primary/40 bg-primary/10 p-5" data-testid="prod-guadagno-20">
                      <div className="flex items-center gap-2 text-primary text-xs font-semibold uppercase tracking-wide"><TrendingUp className="h-4 w-4" /> Guadagno in {summary.anni || 20} anni</div>
                      <div className="text-3xl font-heading font-extrabold mt-1">{eur(summary.guadagno_20anni)}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">Stima cumulata con degrado dei pannelli.</div>
                    </div>
                  </div>

                  <p className="text-[11px] text-muted-foreground">Stima indicativa basata su PVGIS e sui tuoi consumi. Il valore definitivo sarà confermato in consulenza con la configurazione dell'impianto.</p>
                </div>
              )}

              <div className="flex items-center justify-between mt-6">
                <Button variant="ghost" onClick={() => setStep(2)} data-testid="produzione-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro</Button>
                <Button onClick={() => setStep(4)} disabled={prodBusy} data-testid="produzione-next">Avanti <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </div>
            </Card>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-tetto">
            <RoofMap lead={leadStub} selfToken={selfToken} aestheticPanel onUpdate={(l) => setRoofSummary(l.roof?.summary || null)} />
            <div className="mt-4"><EvBenefitEditor lead={{ id: null, cost_estimate: {} }} selfToken={selfToken} /></div>
            {roofSummary ? (
              <Card className="rounded border-primary/40 bg-primary/5 p-5 mt-4" data-testid="tetto-summary">
                <div className="flex items-center gap-2 text-primary font-semibold mb-3"><CheckCircle2 className="h-4 w-4" /> Misurazione salvata</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <SummaryStat label="Superficie proiettata" value={`${num(roofSummary.proiettata_m2)} m²`} />
                  <SummaryStat label="Superficie reale" value={`${num(roofSummary.reale_inclinata_m2)} m²`} />
                  <SummaryStat label="Netta utilizzabile" value={`${num(roofSummary.netta_m2)} m²`} highlight />
                  {roofSummary.moduli_max ? <SummaryStat label="Pannelli installabili" value={roofSummary.moduli_max} highlight /> : null}
                  {roofSummary.potenza_kwp ? <SummaryStat label="Potenza installabile" value={`${num(roofSummary.potenza_kwp)} kWp`} highlight /> : null}
                </div>
                {(roofSummary.moduli_max || roofSummary.potenza_kwp) ? (
                  <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground" data-testid="tetto-summary-note">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px text-amber-500" strokeWidth={2} />
                    <span>Pannelli e potenza indicano il <b>potenziale massimo installabile</b> sulla superficie utile e <b>non considerano ostacoli e ombreggiamenti</b>. Il valore reale sarà definito in fase di dimensionamento dal nostro tecnico.</span>
                  </p>
                ) : null}
                <div className="mt-5 flex justify-end">
                  <Button size="lg" onClick={() => setStep(5)} className="animate-soft-blink font-bold px-6 shadow-lg shadow-primary/40 w-full sm:w-auto" data-testid="tetto-next">Avanti: la tua consulenza <ArrowRight className="h-5 w-5 ml-2" /></Button>
                </div>
              </Card>
            ) : (
              <p className="text-xs text-muted-foreground mt-3">Cerca il tuo indirizzo, disegna le falde del tetto e premi <b>“Salva misurazione”</b> per vedere subito i dati.</p>
            )}
            <Button variant="ghost" onClick={() => setStep(3)} className="mt-2" data-testid="tetto-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Indietro</Button>
          </div>
        )}

        {step === 5 && (
          <>
          <Card className="rounded border-border p-8 text-center" data-testid="step-fine">
            <FileText className="h-12 w-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-heading text-2xl font-extrabold mb-2">Ci siamo!</h1>
            <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Abbiamo tutto il necessario per generare la tua analisi personalizzata. Visualizzala e prenota una consulenza con un nostro esperto.</p>
            <Button onClick={complete} disabled={busy} size="lg" data-testid="fine-genera">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Vedi la mia analisi e prenota <ArrowRight className="h-4 w-4 ml-1.5" /></>}
            </Button>
          </Card>
          <PartnerQuote selfToken={selfToken} />
          </>
        )}

        {DID_YOU_KNOW[step] && <DidYouKnow key={step} data={DID_YOU_KNOW[step]} step={step} />}
      </div>
    </div>
  );
}

function DidYouKnow({ data, step }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`${step === 0 ? "mt-10" : "mt-20 sm:mt-28 border-t border-border pt-12"} rounded-xl border border-sky-500/25 bg-sky-500/5 p-5 sm:p-7`} data-testid="did-you-know">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2.5 text-left" data-testid="dyk-toggle" aria-expanded={open}>
        <Lightbulb className="h-5 w-5 text-sky-500 shrink-0" strokeWidth={2.5} />
        <h3 className="font-heading text-lg sm:text-xl font-extrabold text-sky-500 tracking-tight flex-1">Lo sapevi che?</h3>
        <ChevronDown className={`h-5 w-5 text-sky-500 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={2.5} />
      </button>
      {data.intro && (
        <p className="text-base sm:text-lg font-semibold text-foreground/90 mt-3 max-w-2xl leading-snug" data-testid="dyk-intro">{data.intro}</p>
      )}
      <div className={`grid transition-all duration-300 ease-in-out ${open ? "grid-rows-[1fr] opacity-100 mt-5" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden min-h-0">
          {data.variant === "tech" ? (
            <Card className="rounded border-sky-500/20 bg-sky-500/5 p-6 sm:p-8" data-testid="dyk-tech">
              <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-bold">{data.kicker}</div>
              <h4 className="font-heading font-extrabold text-2xl mt-0.5">{data.title}</h4>
              <p className="text-sm text-muted-foreground mt-2 max-w-xl">{data.subtitle}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                {data.stats.map((s, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="font-heading font-extrabold text-sky-500 text-3xl sm:text-4xl leading-none shrink-0 min-w-[4.5rem] whitespace-nowrap">{s.big}</span>
                    <span className="text-sm text-foreground/90 leading-snug pt-1">{s.label}</span>
                  </div>
                ))}
              </div>
              {data.embed && open && <VideoEmbed embed={data.embed} testid="dyk-embed-tech" />}
            </Card>
          ) : (
            <Card className="relative overflow-hidden rounded border-sky-500/20 bg-sky-500/5 p-6 sm:p-8">
              <div aria-hidden className="pointer-events-none absolute -left-3 -bottom-10 select-none font-heading font-extrabold text-sky-500/10 leading-none text-[8rem] sm:text-[11rem]">{data.big}</div>
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground font-bold">{data.kicker}</div>
                <p className="text-sm text-muted-foreground mt-0.5">{data.subtitle}</p>
                <blockquote className="mt-5 max-w-xl">
                  <p className="font-heading font-bold text-lg sm:text-xl leading-snug">{data.quote}</p>
                  <footer className="text-sm text-muted-foreground italic mt-2">— {data.author}</footer>
                </blockquote>
                {data.embed && open && <VideoEmbed embed={data.embed} testid="dyk-embed" />}
                {data.link && (
                  <a href={data.link} target="_blank" rel="noopener noreferrer" data-testid="dyk-link">
                    <Button variant="outline" size="sm" className="mt-4"><PlayCircle className="h-4 w-4 mr-1.5" /> {data.linkLabel || "Scopri di più"}</Button>
                  </a>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoEmbed({ embed, testid }) {
  const src = `https://www.youtube.com/embed/${embed.id}?start=${embed.start}&end=${embed.end}&rel=0`;
  return (
    <div className="mt-4 max-w-xl aspect-video rounded-lg overflow-hidden border border-border bg-black/40" data-testid={testid}>
      <iframe className="w-full h-full" src={src} title="Anteprima video" frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
    </div>
  );
}

function SummaryStat({ label, value, highlight }) {
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      <div className={`text-lg font-heading font-bold ${highlight ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}
